import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { MessageCircle, ShoppingBag, Archive, ArchiveRestore, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import { useAdminNotifications } from "../../context/AdminNotificationsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending:   "text-amber-400 bg-amber-400/10 border-amber-500/20",
  confirmed: "text-emerald-400 bg-emerald-400/10 border-emerald-500/20",
  cancelled: "text-red-400 bg-red-400/10 border-red-500/20",
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}`;
}

function PreOrderRow({ o, onStatusChange, onArchive, isArchived }) {
  const openWhatsApp = () => {
    const msg = `Hello ${o.customerName}! Your pre-order for ${o.mushroomName} (Qty: ${o.qty}) has been confirmed. We'll contact you soon at this number for delivery details. Thank you for choosing PIIKK Agro Farm Fresh Mushrooms!`;
    window.open(`https://wa.me/91${o.phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <tr className={`hover:bg-white/[0.02] transition-colors ${isArchived ? "opacity-60" : ""}`}
      data-testid={`preorder-row-${o.id}`}>
      <td className="px-4 py-3 text-sm text-[#F2F4F3]">{o.mushroomName}</td>
      <td className="px-4 py-3">
        <p className="text-sm text-[#F2F4F3]">{o.customerName}</p>
        {o.notes && <p className="text-xs text-[#A8BCAE] truncate max-w-xs">{o.notes}</p>}
      </td>
      <td className="px-4 py-3 text-sm text-[#A8BCAE]">{o.phone}</td>
      <td className="px-4 py-3 text-sm font-semibold text-[#D97A46]">{o.qty}</td>
      <td className="px-4 py-3 text-xs text-[#A8BCAE] max-w-[120px] truncate" title={o.address}>{o.address || "—"}</td>
      <td className="px-4 py-3 text-xs text-[#A8BCAE] whitespace-nowrap">{formatDate(o.createdAt)}</td>
      <td className="px-4 py-3">
        {isArchived ? (
          <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${STATUS_COLORS[o.status] || STATUS_COLORS.pending}`}>
            {o.status}
          </span>
        ) : (
          <select
            data-testid={`status-select-${o.id}`}
            value={o.status}
            onChange={e => onStatusChange(o.id, e.target.value)}
            className={`text-xs px-2.5 py-1 rounded-full border font-medium bg-transparent cursor-pointer ${STATUS_COLORS[o.status]}`}
          >
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          {!isArchived && (
            <button onClick={openWhatsApp} data-testid={`whatsapp-customer-${o.id}`}
              className="p-2 rounded-lg text-[#A8BCAE] hover:text-green-400 hover:bg-green-400/10 transition-all" title="Message on WhatsApp">
              <MessageCircle className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onArchive(o.id)}
            data-testid={`${isArchived ? "unarchive" : "archive"}-preorder-${o.id}`}
            className={`p-2 rounded-lg transition-all ${isArchived
              ? "text-[#A8BCAE] hover:text-emerald-400 hover:bg-emerald-400/10"
              : "text-[#A8BCAE] hover:text-amber-400 hover:bg-amber-400/10"
            }`}
            title={isArchived ? "Restore to active" : "Archive (served)"}>
            {isArchived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
          </button>
        </div>
      </td>
    </tr>
  );
}

const HEADERS = ["Mushroom", "Customer", "Phone", "Qty", "Address", "Date", "Status", "Actions"];

function PreOrderTable({ orders, onStatusChange, onArchive, isArchived, emptyMsg }) {
  return (
    <div className="rounded-2xl border border-[#273A2F] overflow-hidden" style={{ backgroundColor: '#16201A' }}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[750px]">
          <thead>
            <tr className="border-b border-[#273A2F]">
              {HEADERS.map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#A8BCAE] uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#273A2F]">
            {orders.map(o => (
              <PreOrderRow key={o.id} o={o} onStatusChange={onStatusChange} onArchive={onArchive} isArchived={isArchived} />
            ))}
          </tbody>
        </table>
      </div>
      {orders.length === 0 && (
        <div className="text-center py-12 text-[#A8BCAE] text-sm">
          <ShoppingBag className="w-7 h-7 mx-auto mb-2 opacity-30" />
          {emptyMsg}
        </div>
      )}
    </div>
  );
}

export default function AdminPreOrdersPage() {
  const { authHeader } = useAuth();
  const { refresh: refreshNotifications } = useAdminNotifications();
  const [activeOrders, setActiveOrders]     = useState([]);
  const [archivedOrders, setArchivedOrders] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [archivesOpen, setArchivesOpen]     = useState(false);
  const [archivesLoaded, setArchivesLoaded] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/admin/preorders?archived=false`, authHeader())
      .then(r => setActiveOrders(r.data))
      .finally(() => setLoading(false));
  }, []); // intentional: fetch once on mount; authHeader() reads localStorage fresh each call

  const loadArchived = useCallback(async () => {
    if (archivesLoaded) return;
    const { data } = await axios.get(`${API}/admin/preorders?archived=true`, authHeader());
    setArchivedOrders(data);
    setArchivesLoaded(true);
  }, [archivesLoaded]);

  const toggleArchiveSection = async () => {
    if (!archivesOpen) await loadArchived();
    setArchivesOpen(v => !v);
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/preorders/${id}`, { status }, authHeader());
    setActiveOrders(o => o.map(x => x.id === id ? { ...x, status } : x));
    refreshNotifications();
  };

  const toggleArchive = async (id) => {
    const { data } = await axios.put(`${API}/admin/preorders/${id}/archive`, {}, authHeader());
    if (data.archived) {
      const item = activeOrders.find(o => o.id === id);
      setActiveOrders(o => o.filter(x => x.id !== id));
      if (archivesLoaded) setArchivedOrders(a => [{ ...item, archived: true }, ...a]);
    } else {
      const item = archivedOrders.find(o => o.id === id);
      setArchivedOrders(a => a.filter(x => x.id !== id));
      setActiveOrders(o => [{ ...item, archived: false }, ...o]);
    }
    refreshNotifications();
  };

  if (loading) return (
    <div className="p-4 sm:p-8">
      <div className="animate-pulse h-64 rounded-2xl border border-[#273A2F]" style={{ backgroundColor: '#16201A' }} />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-preorders-page">

      {/* Header */}
      <div className="mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#F2F4F3]">Pre-Orders</h1>
        <p className="text-sm text-[#A8BCAE] mt-1">
          {activeOrders.length} active &nbsp;·&nbsp; {activeOrders.filter(o => o.status === "pending").length} pending
        </p>
      </div>

      {/* Active Pre-Orders */}
      <PreOrderTable
        orders={activeOrders}
        onStatusChange={updateStatus}
        onArchive={toggleArchive}
        isArchived={false}
        emptyMsg="No active pre-orders."
      />

      {/* ── Archived Pre-Orders (expandable) ── */}
      <div className="mt-6">
        <button
          onClick={toggleArchiveSection}
          data-testid="toggle-archived-preorders-btn"
          className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl border transition-all hover:border-[#D97A46]/30"
          style={{ backgroundColor: '#16201A', borderColor: '#273A2F' }}
        >
          <div className="flex items-center gap-2.5">
            <Archive className="w-4 h-4 text-[#A8BCAE]" />
            <span className="text-sm font-semibold text-[#A8BCAE]">Archived Pre-Orders</span>
            {archivesLoaded && archivedOrders.length > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300">
                {archivedOrders.length}
              </span>
            )}
          </div>
          {archivesOpen
            ? <ChevronUp className="w-4 h-4 text-[#A8BCAE]" />
            : <ChevronDown className="w-4 h-4 text-[#A8BCAE]" />
          }
        </button>

        {archivesOpen && (
          <div className="mt-3" data-testid="archived-preorders-section">
            <PreOrderTable
              orders={archivedOrders}
              onStatusChange={() => {}}
              onArchive={toggleArchive}
              isArchived={true}
              emptyMsg="No archived pre-orders yet."
            />
          </div>
        )}
      </div>

    </div>
  );
}
